import { renderQuoteToPdf } from './renderQuoteToPdf';
import type { QuoteState } from '../types';

/**
 * Block 12 PR-D-A Phase D11 — КП PDF generation + browser download.
 *
 * Pivot Round 4 (Bug B + Bug D): после двух неудачных попыток с html2pdf.js
 * (RAF + offscreen positioning, MSW pause wrapper) PDF оставался 3058 bytes
 * blank из-за конфликта html2canvas iframe ↔ Service Worker scope. Решение —
 * полностью убрать html2canvas / html2pdf.js и рисовать PDF программно через
 * jsPDF (`renderQuoteToPdf`). Никаких iframe, fetch, DOM snapshot — никаких
 * SW conflicts.
 *
 * Bug D также fix: в `renderQuoteToPdf` НЕ включена строка «±10% погрешность».
 *
 * filename: `КП_{quoteName ?? furnitureType ?? 'Без_названия'}_{YYYY-MM-DD}.pdf`.
 */

function formatDateForFilename(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

/**
 * Filename sanitizer — заменяет недопустимые для FS символы (`/`, `\`, `:`, ...)
 * + пробелы на `_`. Гарантирует валидное имя файла на Windows/macOS/Linux.
 */
export function buildPdfFilename(
  quoteName: string | undefined,
  furnitureType: string | null,
  now: Date = new Date(),
): string {
  const raw = quoteName ?? furnitureType ?? 'Без_названия';
  const safe = raw
    .replace(/[\\/:*?"<>|]+/g, '_')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  const base = safe || 'Без_названия';
  return `КП_${base}_${formatDateForFilename(now)}.pdf`;
}

/**
 * Trigger browser download для PDF blob.
 *
 * Exported чтобы переиспользоваться в `shareQuotePdf.ts` (Phase D13 — fallback
 * path для browsers без `navigator.share`). Сохраняет тот же deferred-revoke
 * pattern что и standalone download (Safari/FF race fix).
 */
export function triggerPdfDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  let clickThrew = false;
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noopener';
    anchor.style.display = 'none';
    document.body.appendChild(anchor);
    try {
      anchor.click();
    } catch (err) {
      clickThrew = true;
      throw err;
    } finally {
      anchor.remove();
    }
  } finally {
    // Reviewer Round 4: revoke синхронно может отменить download в Safari/FF —
    // браузер ещё не успел запросить blob после click(). На throw в click()
    // нет смысла откладывать — download не начался.
    if (clickThrew) {
      URL.revokeObjectURL(url);
    } else {
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  }
}

/**
 * Generate PDF от QuoteState и trigger browser download.
 *
 * Side effects:
 *  - DOM: temporarily appends hidden `<a>` для click() trigger, removed
 *    через `anchor.remove()` + `URL.revokeObjectURL` в `finally`.
 *  - Network: один fetch для шрифта PT Sans при первом вызове (per-session
 *    cache в `renderQuoteToPdf`). Никаких других сетевых запросов.
 *  - Browser: triggers download через synthetic anchor click + Blob URL.
 *
 * Errors propagated к caller — wrapping в try/catch + UI toast делает caller.
 */
export async function generateAndDownloadQuotePdf(
  state: QuoteState,
  quoteName?: string,
): Promise<void> {
  const blob = await renderQuoteToPdf(state, quoteName);
  const filename = buildPdfFilename(quoteName, state.furnitureType);
  triggerPdfDownload(blob, filename);
}
